import React, { useEffect, useState } from 'react';
import { Box, Typography, Button, TextField, CircularProgress, LinearProgress } from '@mui/material';
import { vortxTypography } from '@vortx/shared-ui';
import { alpha } from '@mui/material/styles';
import { COLORS, fadeInUp } from '../results/ResultsDesignTokens';

const ModelCard = ({ model, onSelect }) => (
    <Box
        onClick={() => onSelect?.(model)}
        sx={{
            p: 2,
            bgcolor: COLORS.bg.card,
            border: `1px solid ${alpha(COLORS.accent, 0.15)}`,
            borderRadius: 2,
            cursor: onSelect ? 'pointer' : 'default',
            transition: 'all 0.2s ease',
            '&:hover': onSelect ? {
                borderColor: alpha(COLORS.accent, 0.4),
                bgcolor: alpha(COLORS.accent, 0.05),
            } : {},
        }}
    >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography sx={{
                fontFamily: vortxTypography.heading,
                fontSize: '1rem',
                fontWeight: 700,
                color: COLORS.text.primary,
            }}>
                {model.version || model.name}
            </Typography>
            {model.active && (
                <Box sx={{
                    px: 1,
                    py: 0.25,
                    bgcolor: alpha(COLORS.efficiency, 0.15),
                    border: `1px solid ${alpha(COLORS.efficiency, 0.3)}`,
                    borderRadius: 1,
                }}>
                    <Typography sx={{
                        fontFamily: vortxTypography.mono,
                        fontSize: '0.6rem',
                        fontWeight: 600,
                        color: COLORS.efficiency,
                        textTransform: 'uppercase',
                    }}>
                        Active
                    </Typography>
                </Box>
            )}
        </Box>
        {model.created_at && (
            <Typography sx={{
                fontFamily: vortxTypography.mono,
                fontSize: '0.7rem',
                color: COLORS.text.muted,
            }}>
                {new Date(model.created_at).toLocaleDateString()}
            </Typography>
        )}
        {model.components && (
            <Box sx={{ display: 'flex', gap: 0.5, mt: 1, flexWrap: 'wrap' }}>
                {(Array.isArray(model.components) ? model.components : Object.keys(model.components)).map(c => (
                    <Box key={c} sx={{
                        px: 0.75,
                        py: 0.25,
                        bgcolor: alpha(COLORS.accent, 0.1),
                        borderRadius: 0.5,
                    }}>
                        <Typography sx={{
                            fontFamily: vortxTypography.mono,
                            fontSize: '0.6rem',
                            color: COLORS.text.secondary,
                        }}>
                            {c}
                        </Typography>
                    </Box>
                ))}
            </Box>
        )}
    </Box>
);

export default function TrainingTab({ service }) {
    const { models, trainingJob, loading } = service;
    const [showForm, setShowForm] = useState(false);
    const [formVersion, setFormVersion] = useState('');
    const [formTrials, setFormTrials] = useState('50');

    useEffect(() => {
        service.refreshModels();
    }, []);

    const handleStartTraining = () => {
        if (!formVersion.trim()) return;
        service.startTraining({
            model_version: formVersion.trim(),
            optuna_trials: parseInt(formTrials, 10),
        });
        setShowForm(false);
    };

    const modelList = models?.models
        ? Object.entries(models.models).map(([version, data]) => ({
            version,
            ...data,
            active: version === models.active_version,
        }))
        : [];

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Header with action */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography sx={{
                    fontFamily: vortxTypography.mono,
                    fontSize: '0.7rem',
                    color: COLORS.text.muted,
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em',
                }}>
                    {modelList.length} model{modelList.length !== 1 ? 's' : ''} registered
                </Typography>
                <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setShowForm(!showForm)}
                    sx={{
                        fontFamily: vortxTypography.mono,
                        fontSize: '0.7rem',
                        color: COLORS.accent,
                        borderColor: alpha(COLORS.accent, 0.5),
                        '&:hover': {
                            borderColor: COLORS.accent,
                            bgcolor: alpha(COLORS.accent, 0.1),
                        },
                    }}
                >
                    Train New Model
                </Button>
            </Box>

            {/* Training form */}
            {showForm && (
                <Box sx={{
                    p: 2,
                    bgcolor: COLORS.bg.card,
                    border: `1px solid ${alpha(COLORS.accent, 0.3)}`,
                    borderRadius: 2,
                    animation: `${fadeInUp} 0.3s ease-out`,
                }}>
                    <Typography sx={{
                        fontFamily: vortxTypography.mono,
                        fontSize: '0.65rem',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.1em',
                        color: COLORS.text.muted,
                        mb: 2,
                    }}>
                        New Training Job
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                        <TextField
                            label="Version"
                            value={formVersion}
                            onChange={(e) => setFormVersion(e.target.value)}
                            size="small"
                            placeholder="v2"
                            sx={{
                                minWidth: 120,
                                '& .MuiOutlinedInput-root': {
                                    fontFamily: vortxTypography.mono,
                                    fontSize: '0.8rem',
                                    color: COLORS.text.primary,
                                },
                                '& .MuiInputLabel-root': {
                                    fontFamily: vortxTypography.mono,
                                    fontSize: '0.75rem',
                                    color: COLORS.text.muted,
                                },
                            }}
                        />
                        <TextField
                            label="Optuna Trials"
                            value={formTrials}
                            onChange={(e) => setFormTrials(e.target.value)}
                            size="small"
                            type="number"
                            sx={{
                                minWidth: 120,
                                '& .MuiOutlinedInput-root': {
                                    fontFamily: vortxTypography.mono,
                                    fontSize: '0.8rem',
                                    color: COLORS.text.primary,
                                },
                                '& .MuiInputLabel-root': {
                                    fontFamily: vortxTypography.mono,
                                    fontSize: '0.75rem',
                                    color: COLORS.text.muted,
                                },
                            }}
                        />
                        <Button
                            variant="contained"
                            size="small"
                            onClick={handleStartTraining}
                            disabled={loading || !formVersion.trim()}
                            sx={{
                                fontFamily: vortxTypography.mono,
                                fontSize: '0.7rem',
                                bgcolor: COLORS.accent,
                                '&:hover': { bgcolor: alpha(COLORS.accent, 0.8) },
                            }}
                        >
                            Start Training
                        </Button>
                    </Box>
                </Box>
            )}

            {/* Training progress */}
            {trainingJob && trainingJob.status !== 'completed' && trainingJob.status !== 'failed' && (
                <Box sx={{
                    p: 2,
                    bgcolor: COLORS.bg.card,
                    border: `1px solid ${alpha(COLORS.accent, 0.3)}`,
                    borderRadius: 2,
                }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography sx={{
                            fontFamily: vortxTypography.mono,
                            fontSize: '0.7rem',
                            color: COLORS.text.secondary,
                        }}>
                            Training: {trainingJob.job_id}
                        </Typography>
                        <CircularProgress size={16} sx={{ color: COLORS.accent }} />
                    </Box>
                    <LinearProgress
                        variant={trainingJob.progress ? 'determinate' : 'indeterminate'}
                        value={trainingJob.progress || 0}
                        sx={{
                            height: 4,
                            borderRadius: 2,
                            bgcolor: alpha(COLORS.accent, 0.2),
                            '& .MuiLinearProgress-bar': {
                                bgcolor: COLORS.accent,
                            },
                        }}
                    />
                    {trainingJob.phase && (
                        <Typography sx={{
                            fontFamily: vortxTypography.mono,
                            fontSize: '0.65rem',
                            color: COLORS.text.muted,
                            mt: 1,
                        }}>
                            {trainingJob.phase}
                        </Typography>
                    )}
                </Box>
            )}

            {/* Model list */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {modelList.map(model => (
                    <ModelCard key={model.version} model={model} />
                ))}
            </Box>

            {modelList.length === 0 && !loading && (
                <Box sx={{
                    p: 4,
                    textAlign: 'center',
                    bgcolor: COLORS.bg.card,
                    borderRadius: 2,
                    border: `1px solid ${alpha(COLORS.accent, 0.1)}`,
                }}>
                    <Typography sx={{
                        fontFamily: vortxTypography.mono,
                        fontSize: '0.8rem',
                        color: COLORS.text.muted,
                    }}>
                        No models trained yet. Download data and build a sample first.
                    </Typography>
                </Box>
            )}
        </Box>
    );
}
